import { BooleanInput } from '@angular/cdk/coercion';
import {
  booleanAttribute,
  Component,
  input,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  MatCalendar,
  MatDatepickerToggle,
  MatDateRangeInput,
  MatDateRangePicker,
  MatEndDate,
  MatStartDate,
} from '@angular/material/datepicker';
import {
  MatFormField,
  MatFormFieldModule,
  MatLabel,
} from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { DatePipe } from '../../pipes/date.pipe';
import { isRequired } from '../../utilities/form-control.util';
import { ShowField } from '../show-field/show-field.component';

type StartView = 'month' | 'year' | 'multi-year';

type Range = {
  start: Date | null;
  end: Date | null;
};

type InputDateRangeGroup = FormGroup<{
  start: FormControl<Date | null>;
  end: FormControl<Date | null>;
}>;

const initialValues = {
  start: null,
  end: null,
};

@Component({
  selector: 'procure-input-date-range',
  templateUrl: './input-date-range.component.html',
  styleUrl: './input-date-range.component.scss',
  imports: [
    MatFormField,
    MatIcon,
    MatTooltip,
    MatDateRangePicker,
    MatDateRangeInput,
    MatStartDate,
    MatEndDate,
    MatDatepickerToggle,
    ReactiveFormsModule,
    ShowField,
    DatePipe,
    MatLabel,
    MatFormFieldModule,
  ],
  providers: [{ provide: MatCalendar, useValue: { firstDayOfWeek: 1 } }],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class InputDateRange {
  readonly label = input<string>('');
  readonly hint = input<string>('');
  readonly group = input.required<InputDateRangeGroup>();
  readonly startAt = input<Date | null>(null);
  readonly startView = input<StartView>('month');
  readonly id = input<string | null>(null);
  readonly max = input<Date | null>(null);
  readonly min = input<Date | null>(null);
  readonly placeholder = input<string>('');

  readonly showMode = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly disabled = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly clearable = input<boolean, BooleanInput>(true, {
    transform: booleanAttribute,
  });

  protected get value(): Range {
    const group = this.group();

    return {
      start: group.controls.start.value,
      end: group.controls.end.value,
    };
  }

  protected get start(): FormControl<Date | null> {
    return this.group().controls.start;
  }

  protected get end(): FormControl<Date | null> {
    return this.group().controls.end;
  }

  protected get isRequired(): boolean {
    return isRequired(this.start) && isRequired(this.end);
  }

  clear(): void {
    this.group().setValue(initialValues);
  }

  openDatepicker(datepicker: MatDateRangePicker<Date>): void {
    const group = this.group();

    if (this.disabled() || group.disabled) {
      return;
    }

    datepicker.open();
  }
}
